// src/pages/SearchResults.ts
import "../components/TranslatorPlacement.css";
import "./SearchResults.css";

import type { Country } from "../types/Country";
import type { Municipality } from "../types/Municipality";
import type {
  SearchApiResponse,
  SearchResultItem,
} from "../types/SearchResult";

import {
  renderCountryCard,
  initCountryCardEvents,
} from "../components/countries/CountryCard";
import {
  renderMunicipalityCard,
  initMunicipalityCardEvents,
} from "../components/municipalities/MunicipalityCard";
import { renderTimeResultsCard } from "../components/TimeResultsCard";
import { renderTodaysDate, isTodaysDateQuery } from "../components/TodaysDate";
import {
  renderBillionairesCard,
  resolveBillionaireResult,
  renderResolvedBillionaire,
} from "../components/BillionairesCard";
import {
  looksLikeWealthIntelligenceQuery,
  renderWealthIntelligenceCard,
} from "../components/wealth/WealthIntelligence";
import {
  looksLikeMarketDataQuery,
  renderMarketDataCard,
} from "../components/market-data/MarketDataCard";
import { renderWeatherCard } from "../components/WeatherCard";
import { renderWeatherForecastCard } from "../components/WeatherForecastCard";
import { renderWeatherOnDay } from "../components/WeatherOnDay";
import { isNearMeQuery } from "../components/UserLocation";
import { renderCalculatorCard } from "../components/CalculatorCard";
import { renderTranslator } from "../components/Translator";
import { renderDictionaryCard } from "../components/DictionaryCard";
import { renderUnitConverterCard } from "../components/UnitConverterCard";
import { looksLikeCurrencyConversionQuery } from "../components/CurrencyConverter";
import { looksLikeTodaysCurrencyQuery } from "../components/TodaysCurrency";
import { renderAiOverview } from "../components/AiOverview";
import { renderSearchBar } from "../components/SearchBar";
import { renderSearchFooter } from "../components/SearchFooter";
import {
  findAyeOwnedSite,
  isRelevantResultForAyeOwnedSite,
  renderAyeOwnedSiteResult,
} from "../components/AyeOwnedSiteResult";
import {
  type CompanySiteDefinition,
  renderCompanySiteResult,
} from "../components/CompanySiteResult";
import { fetchWebSearch } from "../api/search";
import { resolveAnimal, type AnimalRecord } from "../api/animals";
import { renderAnimalsCard } from "../components/animals/AnimalsCard";
import { resolveVehicleBrand, type VehicleBrandRecord } from "../api/vehicles";
import { renderSimpleBrandCard } from "../components/vehicles/SimpleBrandCard";
import { renderVehicleProducts } from "../components/vehicles/VehicleProducts";
import { renderLocationsCard } from "../components/vehicles/LocationsCard";
import {
  resolveSport,
  type SportRecord,
  type SportsLeagueRecord,
} from "../api/sports";
import { renderSportsCard } from "../components/sports/SportsCard";
import { renderLeagueScheduleCard } from "../components/sports/LeagueScheduleCard";
import { bindLeagueHubCard, renderLeagueHubCard } from "../components/sports/LeagueHubCard";
import { renderProductsRow } from "../components/products";
import { getClientTimeContext } from "../utils/clientTimeContext";

const AI_OVERVIEW_API_URL = "https://api.ayesearch.com/api/ai/overview/";

const MICHAEL_JORDAN_WIKIPEDIA_IMAGE =
  "https://images.ayesearch.com/search/c1/c174867e24053e8d718bc562dd8627a63d1203141945f09575346f44057da332.webp";

const MICHAEL_JORDAN_DESCRIPTION =
  "Michael Jeffrey Jordan (born February 17, 1963) is an American businessman and former professional basketball player. He played 15 seasons in the NBA, won six championships with the Chicago Bulls, earned five league MVP awards, and became one of basketball's most influential and widely recognized players.";

const countries: Record<string, Country> = {};
const municipalities: Municipality[] = [];

const countryFiles = import.meta.glob("../data/countries/*.json", {
  eager: true,
  import: "default",
});

for (const country of Object.values(countryFiles) as Country[]) {
  if (!country || !country.name) continue;
  const slug = country.slug || normalize(country.name);
  countries[slug] = country;
}

const muniFiles = import.meta.glob("../data/japan-municipalities-main/*.json", {
  eager: true,
  import: "default",
});

for (const m of Object.values(muniFiles) as Municipality[]) {
  if (m && typeof m.name === "string") {
    municipalities.push(m);
  }
}

function normalize(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}


function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function focalPercent(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 50;
  }

  return Math.max(0, Math.min(100, value * 100));
}

function isMichaelJordanResult(result: SearchResultItem): boolean {
  const title = (result.title || "").toLowerCase();
  const url = (result.canonical_url || result.url || "").toLowerCase();

  return (
    title.includes("michael jordan") || url.includes("/wiki/michael_jordan")
  );
}

function isMichaelJacksonQuery(query: string): boolean {
  return normalize(query) === "michael-jackson";
}

function tokenizeSearchText(value: string): string[] {
  return String(value || "")
    .toLowerCase()
    .replace(/https?:\/\/(www\.)?/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function isResultRelevantToQuery(
  result: SearchResultItem,
  query: string,
): boolean {
  const queryTokens = tokenizeSearchText(query);
  if (!queryTokens.length) return false;

  const titleTokens = tokenizeSearchText(result.title || "");
  const urlTokens = tokenizeSearchText(
    result.canonical_url || result.url || "",
  );

  const identityTokens = new Set([...titleTokens, ...urlTokens]);

  return queryTokens.every((token) => identityTokens.has(token));
}

function filterRelevantWebResults(
  results: SearchResultItem[],
  query: string,
): SearchResultItem[] {
  return results.filter((result) => isResultRelevantToQuery(result, query));
}

function renderPeopleAlsoSearchFor(query: string): string {
  if (!isMichaelJacksonQuery(query)) {
    return "";
  }

  return `
    <section class="serp-related-section">
      <div class="serp-section-kicker">PEOPLE ALSO SEARCH FOR</div>

      <a
        class="serp-related-card"
        href="/search?q=Michael%20Jordan"
        aria-label="Search for Michael Jordan"
      >
        <img
          class="serp-related-image"
          src="${MICHAEL_JORDAN_WIKIPEDIA_IMAGE}"
          alt="Michael Jordan"
          loading="lazy"
          referrerpolicy="no-referrer"
        />

        <div class="serp-related-copy">
          <strong>Michael Jordan</strong>
          <span>Basketball player</span>
          <p>${escapeHtml(MICHAEL_JORDAN_DESCRIPTION)}</p>
        </div>
      </a>
    </section>
  `;
}

function formatDisplayUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    const path = url.pathname
      .split("/")
      .filter(Boolean)
      .slice(0, 3)
      .map((part) => decodeURIComponent(part))
      .join(" › ");

    return path ? `${url.hostname} › ${path}` : url.hostname;
  } catch {
    return rawUrl;
  }
}

function sourceLabel(rawUrl: string): string {
  try {
    const host = new URL(rawUrl).hostname.replace(/^www\./, "");

    if (host.includes("wikipedia.org")) return "Wikipedia";
    if (host.includes("forbesafrica.com")) return "Forbes Africa";
    if (host.includes("forbes.com")) return "Forbes";
    if (host.includes("aikipedia")) return "Aikipedia";

    const first = host.split(".")[0] || host;
    return first.charAt(0).toUpperCase() + first.slice(1);
  } catch {
    return "Web";
  }
}

function resultBadge(result: SearchResultItem): string {
  let host = "";

  try {
    host = new URL(result.canonical_url || result.url).hostname;
  } catch {
    host = "";
  }

  if (host.includes("aikipedia")) return "AYESEARCH";
  if (host.includes("wikipedia.org")) return "REFERENCE";
  if (host.includes("forbesafrica.com")) return "PUBLISHER";
  if (host.includes("forbes.com")) return "PUBLISHER";
  return "WEB RESULT";
}

function isWeatherQuery(q: string): boolean {
  const w = q.toLowerCase().trim();

  if (w === "weather") return true;

  return (
    w.includes("weather") ||
    w.includes("wheather") ||
    w.includes("what is the weather") ||
    w.includes("what’s the weather") ||
    w.includes("whats the weather") ||
    w.includes("how's the weather") ||
    w.includes("how is the weather") ||
    w.includes("weather today")
  );
}

function isHistoricalWeatherQuery(query: string): boolean {
  if (!isWeatherQuery(query)) return false;

  const value = query.trim();

  // Historical weather is deliberately narrower than generic weather intent.
  // Current/today/near-me/city forecast queries continue through the existing
  // WeatherForecastCard path unchanged.
  return (
    /\b(?:yesterday|last night)\b/i.test(value) ||
    /\b\d{1,2}\s+days?\s+ago\b/i.test(value) ||
    /\b\d{4}-\d{2}-\d{2}\b/.test(value) ||
    /\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+\d{1,2}(?:st|nd|rd|th)?(?:,?\s+\d{4})?\b/i.test(
      value,
    )
  );
}

const WEATHER_SITE_NAVIGATION_PATTERNS = [
  /\bnimet\b/i,
  /\bnaijago\b/i,
  /\bbuild\s+studio\s+ng\b/i,
  /\bweather\s+underground\b/i,
  /\bwunderground\b/i,
  /\btime\s*(?:and|&)\s*date\b/i,
  /\btimeanddate\b/i,
  /\buk\s+met\s+office\b/i,
  /\bmet\s+office\b/i,
  /\baqi(?:\.in)?\b/i,
  /\bclimendo\b/i,
  /\bmeteoblue\b/i,
  /\bworld\s+weather\s+information\s+service\b/i,
  /\bwindy\.app\b/i,
  /\bwindy\s+app\b/i,
  /\bsensoto\b/i,
];

function isWeatherSiteNavigationQuery(query: string): boolean {
  return WEATHER_SITE_NAVIGATION_PATTERNS.some((pattern) =>
    pattern.test(query),
  );
}

function isTimeQuery(query: string): boolean {
  const q = query
    .toLowerCase()
    .trim()
    .replace(/[?!.,]+$/g, "")
    .trim();

  // Keep deterministic time routing strict so brands such as Timeanddate.com
  // continue through normal web search while natural-language clock questions
  // bypass AI Overview entirely.
  if (/\btime\s*(?:and|&)\s*date\b/i.test(q) || /\btimeanddate\b/i.test(q)) {
    return false;
  }

  const currentTimePatterns = [
    /^time$/,
    /^current time$/,
    /^local time$/,
    /^time now$/,
    /^what time is it(?: right now| now| today| here| near me| where i am)?$/,
    /^what(?:'s| is) the time(?: right now| now| today)?$/,
    /^what is the time(?: right now| now| today)?$/,
    /^(?:time|current time|local time|time now) (?:in|for) .+$/,
    /^what time is it(?: right now| now)? (?:in|for) .+$/,
    /^what(?:'s| is) the time(?: right now| now)? (?:in|for) .+$/,
  ];

  const relativeTimePatterns = [
    /^what time (?:was it|was|is it) \d{1,5} (?:minute|minutes|hour|hours|day|days) ago(?: (?:in|for) .+)?(?: near me| here)?$/,
    /^what time (?:was|is) \d{1,5} (?:minute|minutes|hour|hours|day|days) ago(?: (?:in|for) .+)?(?: near me| here)?$/,
    /^what time will it be \d{1,5} (?:minute|minutes|hour|hours|day|days) (?:from now|later)(?: (?:in|for) .+)?$/,
    /^what time is it \d{1,5} (?:minute|minutes|hour|hours|day|days) (?:from now|later)(?: (?:in|for) .+)?$/,
    /^what time is \d{1,5} (?:minute|minutes|hour|hours|day|days) (?:from now|later)(?: (?:in|for) .+)?$/,
  ];

  return [...currentTimePatterns, ...relativeTimePatterns].some((pattern) =>
    pattern.test(q),
  );
}

function weatherLocationHint(query: string): string | null {
  const original = query.trim();
  const lower = original.toLowerCase();

  // “near me” is an instruction to use the device location, never a place
  // name to send to WeatherAPI. Returning null lets WeatherForecastCard use
  // the fresh browser GPS path instead of geocoding the literal words.
  if (isNearMeQuery(original)) return null;

  const directPatterns = [
    /^weather\s+in\s+(.+)$/i,
    /^weather\s+(.+)$/i,
    /^(.+?)\s+weather$/i,
    /^forecast\s+(?:for|in)\s+(.+)$/i,
  ];

  for (const pattern of directPatterns) {
    const match = original.match(pattern);
    const candidate = match?.[1]?.trim();
    if (candidate && !isWeatherSiteNavigationQuery(candidate)) return candidate;
  }

  const providerStripped = original
    .replace(/world\s+weather\s+information\s+service/gi, " ")
    .replace(/weather\s+underground|wunderground/gi, " ")
    .replace(/time\s*(?:and|&)\s*date|timeanddate/gi, " ")
    .replace(/uk\s+met\s+office|met\s+office/gi, " ")
    .replace(/windy\.app|windy\s+app/gi, " ")
    .replace(/meteoblue|climendo|sensoto|aqi(?:\.in)?/gi, " ")
    .replace(/naijago|build\s+studio\s+ng|nimet/gi, " ")
    .replace(
      /seasonal\s+climate\s+prediction|weather\s+center|main\s+portal/gi,
      " ",
    )
    .replace(/forecast(?:s)?|climate|weather/gi, " ")
    .replace(/[–—|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (/\blagos\b/i.test(providerStripped)) return "Lagos, Nigeria";
  if (
    /\bnigeria\b/i.test(providerStripped) ||
    /\bng\b/i.test(providerStripped)
  ) {
    return "Nigeria";
  }

  // Provider-only navigation should show its official result, not geocode the
  // provider's brand name as a city.
  if (isWeatherSiteNavigationQuery(lower)) return null;

  return providerStripped || null;
}

function shouldShowRichWeather(query: string): boolean {
  if (isWeatherQuery(query)) return true;
  return (
    isWeatherSiteNavigationQuery(query) && Boolean(weatherLocationHint(query))
  );
}

function isEnhancedWealthQuery(query: string): boolean {
  const q = query.toLowerCase().trim();
  if (!looksLikeWealthIntelligenceQuery(query)) return false;

  if (
    /\bfounders?\s+of\b/.test(q) ||
    /\bwho\s+(?:is|was|are)\s+(?:the\s+)?founders?\s+of\b/.test(q) ||
    /\bmarket\s*cap(?:italization)?\b/.test(q) ||
    /\bmarket value\b/.test(q)
  ) {
    return true;
  }

  // Preserve the existing generic billionaire/net-worth experience for broad
  // world rankings. The new renderer owns country, heritage and historical
  // ranking intent only.
  return (
    /\b(?:american|nigerian|nigeria|swedish|sweden|indian|india|mexican|mexico|canadian|canada|british|french|german|chinese|china|japanese|japan|south african|african|asian|black|white)\b/.test(
      q,
    ) ||
    /\b(?:ever|historical|all time|all-time)\b/.test(q) ||
    /\blist\b.*\bbillionaires?\b/.test(q)
  );
}

function isBillionaireQuery(query: string): boolean {
  const q = query
    .toLowerCase()
    .trim()
    .replace(/[?!.,]+$/g, "")
    .trim();

  return (
    /\bbillionaires?\b/.test(q) ||
    /\b(?:richest|wealthiest)\s+(?:people|person|men|women|man|woman)\b/.test(
      q,
    ) ||
    /\btop\s+\d{1,3}\s+(?:richest|billionaires?)\b/.test(q) ||
    /\bnet\s+worth\b/.test(q)
  );
}

function looksLikePotentialBillionaireName(query: string): boolean {
  const q = query.trim();
  if (!q || q.length > 80) return false;
  if (!/^[A-Za-zÀ-ÿ0-9.'’\- ]+$/.test(q)) return false;
  const words = q.split(/\s+/).filter(Boolean);
  if (words.length < 2 || words.length > 5) return false;
  return !/\b(weather|time|calculator|convert|currency|define|login|pricing|company|bank|sports|news)\b/i.test(
    q,
  );
}

function isTranslatorQuery(query: string): boolean {
  const q = query
    .toLowerCase()
    .trim()
    .replace(/[?!.,]+$/g, "")
    .trim();

  const directTranslatorQueries = new Set([
    "translator",
    "translate",
    "translation",
    "language translator",
    "ayesearch translator",
    "ayesearch translate",
    "translate ayesearch",
    "translator ayesearch",
    "aye search translator",
    "aye search translate",
    "translate aye search",
    "ayesearch translator contribute",
    "ayesearch translate contribute",
    "ayesearch translator contributor",
    "ayesearch translate contributor",
    "translate ayesearch contributor",
  ]);

  if (directTranslatorQueries.has(q)) {
    return true;
  }

  const language =
    "(?:english|swahili|kiswahili|igbo|hausa|yoruba|yorùbá|arabic)";

  return (
    new RegExp(`^translate\\s+.+\\s+(?:to|into)\\s+${language}$`, "i").test(
      q,
    ) ||
    new RegExp(
      `^${language}\\s+to\\s+${language}\\s+translator(?:\\s*:?.*)?$`,
      "i",
    ).test(q) ||
    new RegExp(
      `^(?:ayesearch|aye\\s+search)\\s+(?:translator|translate)(?:\\s+${language})?$`,
      "i",
    ).test(q) ||
    new RegExp(
      `^(?:translate|translator)\\s+(?:ayesearch|aye\\s+search)(?:\\s+${language})?$`,
      "i",
    ).test(q) ||
    new RegExp(
      `^(?:ayesearch|aye\\s+search)\\s+(?:translator|translate)\\s+(?:contribute|contributor|contribution)$`,
      "i",
    ).test(q) ||
    new RegExp(
      `^(?:translate|translator)\\s+(?:ayesearch|aye\\s+search)\\s+(?:contribute|contributor|contribution)$`,
      "i",
    ).test(q)
  );
}

function isWordTranslationOverviewQuery(query: string): boolean {
  const q = query.trim();
  return (
    // "What is the word for people in Yoruba?"
    /^(?:what(?:'s| is)?\s+)?(?:the\s+)?word\s+for\s+.+\s+in\s+[\p{L}-]+[?.!]*$/iu.test(
      q,
    ) ||
    // "What is the Yoruba word for people?"
    /^(?:what(?:'s| is)?\s+)?(?:the\s+)?[\p{L}-]+\s+word\s+for\s+.+[?.!]*$/iu.test(
      q,
    ) ||
    /^(?:list|show|give)(?:\s+me)?\s+(?:a\s+few|some|\d+)?\s*[\p{L}-]+\s+words?(?:\s+(?:in|to|with)\s+english(?:\s+meanings?)?)?[?.!]*$/iu.test(
      q,
    )
  );
}

function isDictionaryOverviewQuery(query: string): boolean {
  const q = query.toLowerCase().trim();
  return (
    q.startsWith("define ") ||
    q.startsWith("define:") ||
    q.startsWith("definition ") ||
    q.startsWith("dictionary ") ||
    q.startsWith("meaning of ") ||
    q.includes("definition of ") ||
    q.includes("definition for ") ||
    q.startsWith("what is definition ") ||
    q.startsWith("what is the definition ") ||
    q.endsWith(" definition") ||
    q.endsWith(" dictionary") ||
    q.endsWith(" meaning")
  );
}

function classifyQuery(query: string): string {
  const q = query.toLowerCase().trim();

  // Finance intent must be classified before the generic "today" date lane.
  // Otherwise a query such as "What is the market cap of Dangote Cement
  // today?" is incorrectly consumed by TodaysDate simply because it contains
  // the word "today".
  if (looksLikeMarketDataQuery(query)) return "MARKET_DATA_CARD";

  // Currency is deterministic and must beat the generic date/today lane.
  // This prevents "NGN currency price today" from becoming a date card.
  if (looksLikeTodaysCurrencyQuery(query)) return "TODAYS_CURRENCY_CARD";
  if (looksLikeCurrencyConversionQuery(query)) return "CURRENCY_CONVERTER_CARD";

  if (isTodaysDateQuery(query)) return "TODAYS_DATE_CARD";

  if (isWeatherSiteNavigationQuery(query)) return "WEB_SEARCH";
  if (isTimeQuery(query)) return "TIME_CARD";
  if (isWordTranslationOverviewQuery(query)) return "TRANSLATION_AI_OVERVIEW";
  if (isTranslatorQuery(query)) return "TRANSLATOR_CARD";
  if (isHistoricalWeatherQuery(query)) return "WEATHER_ON_DAY_CARD";
  if (isWeatherQuery(q)) return "WEATHER_CARD";
  if (isEnhancedWealthQuery(query)) return "WEALTH_INTELLIGENCE_CARD";
  if (isBillionaireQuery(query)) return "BILLIONAIRES_CARD";
  if (/^[0-9+\-*/(). ]+$/.test(q)) return "CALCULATOR_CARD";

  if (isDictionaryOverviewQuery(query)) return "DICTIONARY_CARD";
  if (q.includes("convert")) return "UNIT_CONVERTER_CARD";

  return "WEB_SEARCH";
}

function detectCountrySlug(query: string): string | null {
  const q = query.trim().toLowerCase();
  const slug = normalize(q);

  if (countries[slug]) return slug;

  for (const [key, country] of Object.entries(countries)) {
    if (country.name.toLowerCase() === q) return key;
  }

  return null;
}

function detectMunicipality(query: string): Municipality | null {
  const lower = query.toLowerCase().trim();

  return (
    municipalities.find(
      (m) => m && typeof m.name === "string" && m.name.toLowerCase() === lower,
    ) || null
  );
}

function renderSearchError(container: HTMLElement, query: string): void {
  container.innerHTML = `
    <div class="serp-empty">
      <div class="serp-empty-kicker">AYESEARCH</div>
      <h1>Search is temporarily unavailable</h1>
      <p>
        We couldn't load web results for
        <strong>${escapeHtml(query)}</strong>.
        Please try again.
      </p>
    </div>
  `;
}

function renderEmptyResults(container: HTMLElement, query: string): void {
  container.innerHTML = `
    <div class="serp-empty">
      <div class="serp-empty-kicker">NO RESULTS YET</div>
      <h1>No indexed pages matched “${escapeHtml(query)}”</h1>
      <p>
        AyeSearch is still growing its live web index.
        Try another phrase or check back after more pages are crawled.
      </p>
    </div>
  `;
}

function renderWebResult(result: SearchResultItem, index: number): string {
  const targetUrl = result.canonical_url || result.url;
  const safeUrl = escapeHtml(targetUrl);
  const jordanResult = isMichaelJordanResult(result);
  const safeTitle = escapeHtml(result.title || targetUrl);
  const safeDescription = escapeHtml(
    jordanResult ? MICHAEL_JORDAN_DESCRIPTION : result.description || "",
  );

  const sourceName = result.source_name || sourceLabel(targetUrl);

  const safeSource = escapeHtml(sourceName);
  const safeDisplayUrl = escapeHtml(formatDisplayUrl(targetUrl));
  const safeBadge = escapeHtml(resultBadge(result));
  const focalX = focalPercent(result.image_focal_x);
  const focalY = focalPercent(result.image_focal_y);

  const faviconUrl = result.favicon_url || result.favicon_source_url || "";

  const displayImageUrl = jordanResult
    ? MICHAEL_JORDAN_WIKIPEDIA_IMAGE
    : result.image_url;

  const sourceIconHtml = faviconUrl
    ? `
      <img
        class="serp-source-favicon"
        src="${escapeHtml(faviconUrl)}"
        alt=""
        loading="${index === 0 ? "eager" : "lazy"}"
        referrerpolicy="no-referrer"
        data-fallback-letter="${escapeHtml(
          safeSource.slice(0, 1).toUpperCase(),
        )}"
      />
    `
    : `
      <span class="serp-source-letter">
        ${safeSource.slice(0, 1).toUpperCase()}
      </span>
    `;

  const imageHtml = displayImageUrl
    ? `
      <a
        class="serp-result-image-link"
        href="${safeUrl}"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open ${safeTitle}"
      >
        <img
          class="serp-result-image"
          src="${escapeHtml(displayImageUrl)}"
          alt=""
          loading="${index === 0 ? "eager" : "lazy"}"
          referrerpolicy="no-referrer"
          style="object-position: ${focalX.toFixed(2)}% ${focalY.toFixed(2)}%;"
        />
      </a>
    `
    : "";

  return `
    <article class="serp-result ${displayImageUrl ? "has-image" : ""}">
      <div class="serp-result-copy">
        <div class="serp-result-badge">${safeBadge}</div>

        <a
          class="serp-source-row"
          href="${safeUrl}"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="serp-source-mark">
            ${sourceIconHtml}
          </span>

          <span class="serp-source-meta">
            <strong>${safeSource}</strong>
            <span>${safeDisplayUrl}</span>
          </span>
        </a>

        <h2 class="serp-result-title">
          <a
            href="${safeUrl}"
            target="_blank"
            rel="noopener noreferrer"
          >
            ${safeTitle}
          </a>
        </h2>

        ${
          safeDescription
            ? `<p class="serp-result-snippet">${safeDescription}</p>`
            : ""
        }
      </div>

      ${imageHtml}
    </article>
  `;
}

function renderWebSearchResults(
  container: HTMLElement,
  response: SearchApiResponse,
  enforceQueryRelevance: boolean = true,
): void {
  const baseResults = enforceQueryRelevance
    ? filterRelevantWebResults(response.results, response.query)
    : response.results;

  const visibleResults = isMichaelJacksonQuery(response.query)
    ? baseResults.filter((result) => !isMichaelJordanResult(result))
    : baseResults;

  if (!visibleResults.length) {
    renderEmptyResults(container, response.query);
    return;
  }

  const visibleCount = visibleResults.length;
  const countLabel =
    visibleCount === 1
      ? "1 indexed result"
      : `${visibleCount.toLocaleString()} indexed results`;

  container.innerHTML = `
    <div class="serp-results-meta">
      <span>${escapeHtml(countLabel)}</span>
      <span class="serp-results-dot">•</span>
      <span>Live AyeSearch index</span>
    </div>

    <div class="serp-results-feed">
      ${visibleResults.map(renderWebResult).join("")}
    </div>
  `;

  const favicons = container.querySelectorAll<HTMLImageElement>(
    ".serp-source-favicon",
  );

  favicons.forEach((favicon) => {
    favicon.addEventListener("error", () => {
      const mark = favicon.closest(".serp-source-mark");
      if (!mark) return;

      const fallbackLetter = favicon.dataset.fallbackLetter || "?";

      mark.innerHTML = `
        <span class="serp-source-letter">
          ${escapeHtml(fallbackLetter)}
        </span>
      `;
    });
  });

  const images =
    container.querySelectorAll<HTMLImageElement>(".serp-result-image");

  images.forEach((image) => {
    image.addEventListener("error", () => {
      const imageLink = image.closest(".serp-result-image-link");
      imageLink?.remove();

      const result = image.closest(".serp-result");
      result?.classList.remove("has-image");
    });
  });
}

async function loadWebResults(
  container: HTMLElement,
  query: string,
  featuredContainer?: HTMLElement | null,
  aiContainer?: HTMLElement | null,
  aiSection?: HTMLElement | null,
  allowAiOverview: boolean = true,
): Promise<void> {
  try {
    const response = await fetchWebSearch(query);
    const ayeOwnedSite = findAyeOwnedSite(query);

    const apiResponse = response as SearchApiResponse & {
      official_site?: CompanySiteDefinition | null;
      domain_media?: {
        domain?: string;
        url?: string;
        canonical_url?: string;
        favicon_url?: string;
        favicon_source_url?: string;
        image_url?: string;
        fetched_at?: string | null;
        indexed_at?: string | null;
      } | null;
    };

    const officialSite = apiResponse.official_site || null;
    const domainMedia = apiResponse.domain_media || null;

    // External company results intentionally do not show or request AI Overview.
    // Non-company searches reveal and load AI only after the backend response
    // confirms there is no official company result.
    if (officialSite || !allowAiOverview) {
      aiSection?.remove();
    } else if (aiSection && aiContainer) {
      aiSection.removeAttribute("hidden");
      void loadAiOverview(aiContainer, query);
    }

    if (ayeOwnedSite) {
      const relevantResults = response.results.filter((result) =>
        isRelevantResultForAyeOwnedSite(ayeOwnedSite, result),
      );

      const primaryResult = relevantResults[0];

      if (featuredContainer) {
        const featuredSection =
          featuredContainer.closest<HTMLElement>("section[hidden]") ||
          document.getElementById("serp-featured-site-section");

        featuredSection?.removeAttribute("hidden");

        featuredContainer.innerHTML = renderAyeOwnedSiteResult(ayeOwnedSite, {
          faviconUrl:
            domainMedia?.favicon_url ||
            domainMedia?.favicon_source_url ||
            primaryResult?.favicon_url ||
            primaryResult?.favicon_source_url ||
            "",
          imageUrl: domainMedia?.image_url || primaryResult?.image_url || "",
        });
      }

      if (!relevantResults.length) {
        container.innerHTML = `
          <div class="serp-web-loading-note">
            No additional relevant indexed pages yet.
          </div>
        `;
        return;
      }

      renderWebSearchResults(container, {
        ...response,
        count: relevantResults.length,
        results: relevantResults,
      });
      return;
    }

    if (officialSite) {
      if (featuredContainer) {
        const featuredSection =
          featuredContainer.closest<HTMLElement>("section[hidden]") ||
          document.getElementById("serp-featured-site-section");

        featuredSection?.removeAttribute("hidden");
        featuredContainer.innerHTML = renderCompanySiteResult(officialSite);
      }

      const officialDomain = (officialSite.domain || "")
        .toLowerCase()
        .replace(/^www\./, "");

      const resultDomains = Array.from(
        new Set(
          [
            officialDomain,
            ...(Array.isArray(officialSite.result_domains)
              ? officialSite.result_domains
              : []),
          ]
            .map((domain) =>
              String(domain || "")
                .toLowerCase()
                .trim()
                .replace(/^https?:\/\//, "")
                .replace(/^www\./, "")
                .replace(/\/.*$/, ""),
            )
            .filter(Boolean),
        ),
      );

      const domainRank = (result: SearchResultItem): number => {
        try {
          const targetUrl = result.canonical_url || result.url || "";

          const host = new URL(targetUrl).hostname
            .toLowerCase()
            .replace(/^www\./, "");

          const rank = resultDomains.findIndex(
            (domain) => host === domain || host.endsWith(`.${domain}`),
          );

          return rank === -1 ? Number.MAX_SAFE_INTEGER : rank;
        } catch {
          return Number.MAX_SAFE_INTEGER;
        }
      };

      const relevantResults = response.results
        .filter((result) => domainRank(result) !== Number.MAX_SAFE_INTEGER)
        .sort((a, b) => domainRank(a) - domainRank(b));

      if (!relevantResults.length) {
        container.innerHTML = `
          <div class="serp-web-loading-note">
            No additional relevant indexed pages yet.
          </div>
        `;
        return;
      }

      renderWebSearchResults(
        container,
        {
          ...response,
          count: relevantResults.length,
          results: relevantResults,
        },
        false,
      );
      return;
    }

    renderWebSearchResults(container, response);
  } catch (error) {
    console.error("AyeSearch web search failed:", error);
    renderSearchError(container, query);
  }
}

async function loadAiOverview(
  container: HTMLElement,
  query: string,
): Promise<void> {
  container.innerHTML = `
    <div class="serp-ai-loading">
      Generating AI overview for <strong>${escapeHtml(query)}</strong>...
    </div>
  `;

  try {
    const url = new URL(AI_OVERVIEW_API_URL);
    url.searchParams.set("q", query);

    const { timezone, locale, timezoneOffsetMinutes } = getClientTimeContext();
    if (timezone) url.searchParams.set("timezone", timezone);
    if (locale) url.searchParams.set("locale", locale);
    url.searchParams.set(
      "timezone_offset_minutes",
      String(timezoneOffsetMinutes),
    );

    const response = await fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`AI Overview returned HTTP ${response.status}`);
    }

    const data = await response.json();

    if (!data) {
      throw new Error("Empty AI overview response");
    }

    renderAiOverview(container, data, { query });

    // Translator stays outside AI Overview as a separate search-result block.
    if (isWordTranslationOverviewQuery(query)) {
      const translatorResult = document.createElement("div");
      translatorResult.className = "serp-translator-result";
      container.insertAdjacentElement("afterend", translatorResult);
      void renderTranslator(translatorResult, query, {
        includeExtras: false,
      });
    }
  } catch (error) {
    console.error("AyeSearch AI overview failed:", error);
    container.innerHTML = `
      <div class="serp-ai-error">
        AI overview is temporarily unavailable for
        <strong>${escapeHtml(query)}</strong>.
      </div>
    `;
  }
}

function renderRealSearchExperience(
  main: HTMLElement,
  query: string,
  options: { allowAiOverview?: boolean } = {},
): void {
  const allowAiOverview = options.allowAiOverview !== false;
  main.innerHTML = `
    <section class="serp-shell">
      <div class="serp-query-summary">
        Showing AyeSearch results for <strong>${escapeHtml(query)}</strong>
      </div>

      <section
        class="serp-section serp-owned-site-section"
        id="serp-featured-site-section"
        hidden
      >
        <div id="serp-featured-site"></div>
      </section>

      <section
        class="serp-section serp-ai-section"
        id="serp-ai-section"
        hidden
      >
        <div class="serp-section-kicker">AI OVERVIEW</div>
        <div class="serp-ai-card" id="serp-ai-overview"></div>
      </section>

      <section class="serp-section serp-web-section">
        <div class="serp-section-kicker">WEB RESULTS</div>
        <div id="serp-web-results">
          <div class="serp-loading-list">
            <div class="serp-web-loading-note">
              Loading live indexed results...
            </div>
            <div class="serp-skeleton"></div>
            <div class="serp-skeleton"></div>
            <div class="serp-skeleton"></div>
          </div>
        </div>
      </section>

      ${renderPeopleAlsoSearchFor(query)}
    </section>
  `;

  const aiContainer = document.getElementById("serp-ai-overview");
  const aiSection = document.getElementById("serp-ai-section");
  const webContainer = document.getElementById("serp-web-results");
  const featuredContainer = document.getElementById("serp-featured-site");

  // Load web results first so the backend can decide whether this is a
  // curated official-company result. Company results never start AI Overview.
  if (webContainer) {
    void loadWebResults(
      webContainer,
      query,
      featuredContainer,
      aiContainer,
      aiSection,
      allowAiOverview,
    );
  }
}

function renderSportSearchExperience(
  main: HTMLElement,
  query: string,
  sport: SportRecord,
): void {
  const showCard = sport.presentation?.show_card !== false;
  const allowAiOverview =
    !showCard && sport.presentation?.show_ai_overview === true;

  // No-card mode deliberately renders the normal SERP shell.
  // The registry decides whether AI Overview is allowed in that mode.
  if (!showCard) {
    renderRealSearchExperience(main, query, {
      allowAiOverview,
    });
    return;
  }

  const showProducts = sport.slug === "table-tennis";

  main.innerHTML = `
    <div class="sport-search-experience">
      <section class="sport-result-wrapper">
        ${renderSportsCard(sport)}
      </section>
      ${showProducts ? '<section class="table-tennis-products-result" id="table-tennis-products-result"></section>' : ""}
      <div class="sport-search-serp" id="sport-search-serp"></div>
    </div>
  `;

  if (showProducts) {
    const productsContainer = document.getElementById(
      "table-tennis-products-result",
    );
    if (productsContainer) {
      void renderProductsRow(productsContainer, "table-tennis");
    }
  }

  const serpContainer = document.getElementById("sport-search-serp");
  if (serpContainer) {
    // Any visible SportsCard owns the structured answer. Keep ordinary web
    // results underneath it, but never request generic AI Overview.
    renderRealSearchExperience(serpContainer, query, {
      allowAiOverview: false,
    });
  }
}


function renderLeagueSearchExperience(
  main: HTMLElement,
  query: string,
  league: SportsLeagueRecord,
): void {
  const showCard = league.presentation?.show_card !== false;
  const allowAiOverview =
    !showCard && league.presentation?.show_ai_overview === true;

  if (!showCard) {
    renderRealSearchExperience(main, query, {
      allowAiOverview,
    });
    return;
  }

  const useFreeHub = league.hub?.enabled === true;

  main.innerHTML = `
    <div class="sport-search-experience">
      <section class="sport-result-wrapper">
        ${useFreeHub ? renderLeagueHubCard(league) : renderLeagueScheduleCard(league)}
      </section>
      <div class="sport-search-serp" id="sport-league-search-serp"></div>
    </div>
  `;

  if (useFreeHub) {
    bindLeagueHubCard(league);
  }

  const serpContainer = document.getElementById("sport-league-search-serp");
  if (serpContainer) {
    // The deterministic league schedule card owns the structured sports answer.
    // Ordinary web results still render underneath, but generic AI Overview is
    // never requested while the league card is visible.
    renderRealSearchExperience(serpContainer, query, {
      allowAiOverview: false,
    });
  }
}


function renderVehicleBrandSearchExperience(
  main: HTMLElement,
  query: string,
  brand: VehicleBrandRecord,
): void {
  // Vehicle queries are already resolved before the generic company SERP lane.
  // Give loadWebResults a dedicated featured-site target ABOVE the vehicle card
  // so backend official_sites.json entries (Lamborghini, Ferrari, etc.) remain
  // visible without changing the vehicle resolver or duplicating web requests.
  main.innerHTML = `
    <div class="vehicle-brand-search-experience">
      <section
        class="vehicle-brand-result-wrapper serp-section"
        id="vehicle-brand-official-site-section"
        hidden
      >
        <div id="vehicle-brand-official-site"></div>
      </section>

      <section class="vehicle-brand-result-wrapper">
        ${renderSimpleBrandCard(brand)}
      </section>

      <section
        class="vehicle-products-result"
        id="vehicle-products-result"
      ></section>

      <section
        class="vehicle-locations-result"
        id="vehicle-locations-result"
      ></section>

      <section class="serp-shell vehicle-brand-web-results">
        <div class="serp-query-summary">
          Showing AyeSearch results for <strong>${escapeHtml(query)}</strong>
        </div>

        <section class="serp-section serp-web-section">
          <div class="serp-section-kicker">WEB RESULTS</div>
          <div id="vehicle-brand-web-results">
            <div class="serp-loading-list">
              <div class="serp-web-loading-note">
                Loading live indexed results...
              </div>
              <div class="serp-skeleton"></div>
              <div class="serp-skeleton"></div>
              <div class="serp-skeleton"></div>
            </div>
          </div>
        </section>
      </section>
    </div>
  `;

  const productsContainer = document.getElementById("vehicle-products-result");
  const locationsContainer = document.getElementById("vehicle-locations-result");
  const webContainer = document.getElementById("vehicle-brand-web-results");
  const featuredContainer = document.getElementById("vehicle-brand-official-site");

  if (productsContainer) {
    void renderVehicleProducts(productsContainer, brand.slug);
  }

  if (locationsContainer) {
    renderLocationsCard(locationsContainer, brand.slug);
  }

  if (webContainer) {
    // Reuse the canonical company-site/web-result loader. This preserves the
    // backend-owned official-site catalog, result-domain filtering and favicon
    // behavior while keeping AI Overview disabled for the structured vehicle lane.
    void loadWebResults(
      webContainer,
      query,
      featuredContainer,
      null,
      null,
      false,
    );
  }
}


function renderAnimalSearchExperience(
  main: HTMLElement,
  query: string,
  animal: AnimalRecord,
): void {
  main.innerHTML = `
    <div class="animal-search-experience">
      <section class="animal-result-wrapper">
        ${renderAnimalsCard(animal)}
      </section>
      <div class="animal-search-serp" id="animal-search-serp"></div>
    </div>
  `;

  const serpContainer = document.getElementById("animal-search-serp");
  if (serpContainer) {
    // A registered animal owns the structured answer. Keep ordinary indexed
    // web results underneath it, but never start generic AI Overview.
    renderRealSearchExperience(serpContainer, query, {
      allowAiOverview: false,
    });
  }
}

function renderHistoricalWeatherSearchExperience(
  main: HTMLElement,
  query: string,
): void {
  main.innerHTML = `
    <div class="weather-results-page weather-on-day-results-page">
      <section class="weather-results-module" id="weather-on-day-module"></section>
      <div class="weather-results-serp" id="weather-on-day-serp"></div>
    </div>
  `;

  const weatherContainer = document.getElementById("weather-on-day-module");
  const serpContainer = document.getElementById("weather-on-day-serp");

  if (weatherContainer) {
    void renderWeatherOnDay(weatherContainer, query);
  }

  // Historical weather owns its deterministic AyeSearch-history answer.
  // Keep ordinary indexed web results below it, but never start generic AI
  // Overview and never mount the live/current WeatherForecastCard.
  if (serpContainer) {
    renderRealSearchExperience(serpContainer, query, {
      allowAiOverview: false,
    });
  }
}

function renderWeatherSearchExperience(main: HTMLElement, query: string): void {
  const locationHint = weatherLocationHint(query);

  main.innerHTML = `
    <div class="weather-results-page">
      <section class="weather-results-module" id="weather-results-module"></section>
      <div class="weather-results-serp" id="weather-results-serp"></div>
    </div>
  `;

  const weatherContainer = document.getElementById("weather-results-module");
  const serpContainer = document.getElementById("weather-results-serp");

  if (weatherContainer) {
    void renderWeatherForecastCard(weatherContainer, query, {
      locationOverride: locationHint || undefined,
      // Near-me weather owns its complete GPS -> IP fallback chain inside
      // WeatherForecastCard/UserLocation. Do not fall through to the legacy
      // WeatherCard, which can revive stale pre-v2 location state.
      fallbackRenderer: isNearMeQuery(query)
        ? undefined
        : (container, fallbackQuery) =>
            renderWeatherCard(container, fallbackQuery),
    });
  }

  if (serpContainer) {
    renderRealSearchExperience(serpContainer, query, {
      allowAiOverview: false,
    });
  }
}

export function renderSearchResults(query: string = ""): void {
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => renderSearchResults(query),
      { once: true },
    );
    return;
  }

  const app = document.getElementById("app");
  if (!app) return;

  const normalizedQuery = normalize(query);

  app.innerHTML = `
    <div class="results-container">
      <div
        id="search-bar-container"
        class="searchbar-container"
      ></div>
      <main class="results-main" id="results-main"></main>
      <div id="search-footer-container"></div>
    </div>
  `;

  const main = document.getElementById("results-main")!;
  const searchBarContainer = document.getElementById("search-bar-container")!;

  renderSearchBar(searchBarContainer, query);

  const footerContainer = document.getElementById("search-footer-container");
  if (footerContainer) {
    renderSearchFooter(footerContainer);
  }

  if (!query.trim()) {
    renderEmptyResults(main, query);
    return;
  }

  const queryType = classifyQuery(query);

  if (queryType === "TODAYS_DATE_CARD") {
    // Deterministic date lane: local browser calendar + AyeSearch location
    // context only. No Together AI, AI Overview, or web-generation request.
    void renderTodaysDate(main, query);
    return;
  }

  if (queryType === "TIME_CARD") {
    // Deterministic utility lane: do not render, request, or meter AI Overview
    // for direct/relative time searches. The original TimeCard remains intact
    // as a network/error fallback.
    void renderTimeResultsCard(main, query);
    return;
  }

  if (queryType === "TRANSLATOR_CARD") {
    // Deterministic language lane: translator searches never request AI Overview.
    void renderTranslator(main, query);
    return;
  }

  if (queryType === "MARKET_DATA_CARD") {
    // Nigerian/NGX market-data lane. Try the dedicated market_data backend
    // first. A miss deliberately falls back to the existing wealth card so
    // Apple/Google/Amazon seeded market-cap behavior remains unchanged.
    void (async () => {
      const handled = await renderMarketDataCard(main, query);
      if (handled) return;

      const legacyHandled = await renderWealthIntelligenceCard(main, query);
      if (legacyHandled) return;

      renderRealSearchExperience(main, query);
    })();
    return;
  }

  if (queryType === "WEALTH_INTELLIGENCE_CARD") {
    // Additive wealth/entity lane. A curated miss falls through without
    // changing the existing broad billionaire or normal SERP behavior.
    void (async () => {
      const handled = await renderWealthIntelligenceCard(main, query);
      if (handled) return;

      // A scoped country/heritage/historical miss must not silently degrade
      // into the generic world-billionaire card. Let the normal SERP + AI
      // Overview answer instead.
      renderRealSearchExperience(main, query);
    })();
    return;
  }

  if (queryType === "BILLIONAIRES_CARD") {
    // Wealth lane: serve AyeSearch's cached/snapshotted billionaire data.
    // The browser never requests Forbes directly and AI Overview is never
    // started for a recognized billionaire result.
    void (async () => {
      const handled = await renderBillionairesCard(main, query);
      if (!handled) {
        renderRealSearchExperience(main, query);
      }
    })();
    return;
  }

  if (queryType === "WEATHER_ON_DAY_CARD") {
    // Historical weather is a retained-cache lookup, not a live forecast.
    // It must never mount WeatherForecastCard or current-device location UI.
    renderHistoricalWeatherSearchExperience(main, query);
    return;
  }

  if (queryType === "WEATHER_CARD" || shouldShowRichWeather(query)) {
    // WeatherForecastCard owns current-device freshness, permission and the
    // friendly city resolver. Do not treat a legacy localStorage string as
    // sufficient proof of current location here.
    renderWeatherSearchExperience(main, query);
    return;
  }

  if (queryType === "CALCULATOR_CARD") {
    renderCalculatorCard(main, query);
    return;
  }

  if (queryType === "DICTIONARY_CARD") {
    // Original deterministic dictionary lane. Definition/define/meaning searches
    // intentionally remain separate from the Translator AI Overview pipeline.
    void renderDictionaryCard(main, query);
    return;
  }

  if (queryType === "TRANSLATION_AI_OVERVIEW") {
    renderRealSearchExperience(main, query);
    return;
  }

  if (queryType === "UNIT_CONVERTER_CARD") {
    renderUnitConverterCard(main, query);
    return;
  }

  if (
    queryType === "TODAYS_CURRENCY_CARD" ||
    queryType === "CURRENCY_CONVERTER_CARD"
  ) {
    // Currency remains deterministic, but now participates in the normal SERP
    // so AI Overview can host the existing interactive card, Show more, rich
    // source presentation and ordinary indexed web results beneath it.
    renderRealSearchExperience(main, query);
    return;
  }

  const countrySlug = detectCountrySlug(query);

  if (countrySlug) {
    const country = countries[countrySlug];

    if (country) {
      const wrapper = document.createElement("div");
      wrapper.className = "country-result-wrapper";
      wrapper.innerHTML = renderCountryCard(country);
      main.appendChild(wrapper);
      initCountryCardEvents(wrapper);
      return;
    }
  }

  const municipality = detectMunicipality(query);

  if (municipality) {
    const wrapper = document.createElement("div");
    wrapper.className = "municipality-result-wrapper";
    wrapper.innerHTML = renderMunicipalityCard(municipality);
    main.appendChild(wrapper);
    initMunicipalityCardEvents(wrapper);
    return;
  }

  // Vehicle brands are resolved from the dedicated JSON-backed vehicle registry.
  // A miss continues into the existing Sports/Animals/Billionaires pipeline.
  void (async () => {
    try {
      const resolvedVehicleBrand = await resolveVehicleBrand(query);
      if (resolvedVehicleBrand.matched && resolvedVehicleBrand.brand) {
        renderVehicleBrandSearchExperience(
          main,
          query,
          resolvedVehicleBrand.brand,
        );
        return;
      }
    } catch (error) {
      console.error("AyeSearch vehicle brand lookup failed:", error);
    }

    // Registered Sports resolve before Animals. Presentation is registry-driven:
    // - show_card=true  -> SportsCard + ordinary web results, never AI Overview.
    // - show_card=false + show_ai_overview=true -> normal SERP + AI Overview.
    // - show_card=false + show_ai_overview=false -> normal SERP, no AI Overview.
    // A Sports miss continues into the existing Animals/Billionaires pipeline.
    try {
      const resolvedSport = await resolveSport(query);
      if (
        resolvedSport.matched &&
        resolvedSport.resolved_type === "league" &&
        resolvedSport.league
      ) {
        renderLeagueSearchExperience(main, query, resolvedSport.league);
        return;
      }

      if (
        resolvedSport.matched &&
        resolvedSport.resolved_type === "sport" &&
        resolvedSport.sport
      ) {
        renderSportSearchExperience(main, query, resolvedSport.sport);
        return;
      }
    } catch (error) {
      console.error("AyeSearch sports lookup failed:", error);
    }

    try {
      const resolved = await resolveAnimal(query);
      if (resolved.matched && resolved.animal) {
        renderAnimalSearchExperience(main, query, resolved.animal);
        return;
      }
    } catch (error) {
      console.error("AyeSearch animal lookup failed:", error);
    }

    continueSearchAfterAnimalLookup(main, query, normalizedQuery);
  })();
  return;
}

function continueSearchAfterAnimalLookup(
  main: HTMLElement,
  query: string,
  normalizedQuery: string,
): void {
  // Plain billionaire names (for example "Jeff Bezos" or "Larry Page")
  // do not contain words such as "billionaire" or "net worth". Resolve these
  // against AyeSearch's local Top-100 cache BEFORE normal SERP/AI Overview.
  // A miss simply continues into the existing search pipeline.
  if (looksLikePotentialBillionaireName(query)) {
    void (async () => {
      try {
        const data = await resolveBillionaireResult(query);
        if (data && renderResolvedBillionaire(main, data, query)) return;
      } catch (error) {
        console.error("AyeSearch billionaire name lookup failed:", error);
      }
      renderSearchResultsAfterBillionaireLookup(main, query, normalizedQuery);
    })();
    return;
  }

  renderSearchResultsAfterBillionaireLookup(main, query, normalizedQuery);
}

function renderSearchResultsAfterBillionaireLookup(
  main: HTMLElement,
  query: string,
  _normalizedQuery: string,
): void {
  // NBA player cards are intentionally deprecated for now.
  // After deterministic entity resolvers (including Animals and Billionaires)
  // miss, continue directly to the ordinary web SERP instead of fuzzy-matching
  // an NBA player name. This prevents queries such as "lion" from becoming
  // Lionel Chalmers and keeps generic/company/animal misses on normal search.
  renderRealSearchExperience(main, query);
}
